<template>
  <div v-if="hasAnyPermission('sap-sync-task-view')">
    <div class="py-4 container-fluid">
      <ToastPrime position="bottom-left" />
      <div class="row">
        <div class="col-12">
          <div class="card">
            <!-- Card header -->
            <div class="card-header py-3">
              <div class="d-lg-flex align-items-center row">
                <div class="col-6 float-start">
                  <h5 class="mb-0">SAP Synchron Task</h5>
                </div>
                <div class="col-6 d-flex justify-content-end">
                  <button class="btn btn-dark btn-sm my-0 align-end" @click="openFilter">Filter</button>
                </div>
              </div>
            </div>
            <!-- Card Body -->
            <div style="height: 500px; overflow-y: auto">
              <DataTable :value="table.data" removable-sort scrollable scroll-height="flex"
                table-style="min-width: 100rem" resizable-columns column-resize-mode="expand">
                <Column header="SAP Sync Code" header-class="bg-light" sortable style="width: 220px">
                  <template #body="row">
                    <Skeleton v-if="table.onLoad" />
                    <span v-else>{{ row.data.sap_sync_code }}</span>
                  </template>
                </Column>
                <Column header="Connection" header-class="bg-light" sortable style="width: 260px">
                  <template #body="row">
                    <Skeleton v-if="table.onLoad" />
                    <span v-else>{{ row.data.connection }}</span>
                  </template>
                </Column>
                <Column header="Key" header-class="bg-light" sortable style="width: 150px">
                  <template #body="row">
                    <Skeleton v-if="table.onLoad" />
                    <span v-else>{{ row.data.emcr?.mcr_number ?? "-" }}</span>
                  </template>
                </Column>
                <Column header="Status" header-class="bg-light" sortable style="width: 120px">
                  <template #body="row">
                    <Skeleton v-if="table.onLoad" />
                    <span v-else class="text-uppercase"
                      :class="row.data.status === '1' ? 'badge badge-success' : row.data.status === '-1' ? 'badge badge-danger' : 'badge badge-secondary'">
                      {{ row.data.status === '1' ? 'Success' : row.data.status === '-1' ? 'Failed' : 'Open' }}
                    </span>
                  </template>
                </Column>
                <Column header="Requested By" header-class="bg-light" sortable style="width: 220px">
                  <template #body="row">
                    <Skeleton v-if="table.onLoad" />
                    <span v-else>{{ row.data.requested_by_detail?.name ?? '-' }}</span>
                  </template>
                </Column>
                <Column header="Requested At" header-class="bg-light" sortable style="width: 180px">
                  <template #body="row">
                    <Skeleton v-if="table.onLoad" />
                    <span v-else>{{ formatDate(row.data.requested_at) }}</span>
                  </template>
                </Column>
                <Column header="Done At" header-class="bg-light" sortable style="width: 180px">
                  <template #body="row">
                    <Skeleton v-if="table.onLoad" />
                    <span v-else>{{ formatDate(row.data.done_at) }}</span>
                  </template>
                </Column>
                <Column header="Actions" header-class="bg-light text-dark" style="width: 140px">
                  <template v-if="table.onLoad" #body>
                    <Skeleton />
                  </template>
                  <template v-else #body="row">
                    <div v-if="table.data.length > 0 && !table.onLoad">
                      <ButtonPrime
                        v-if="hasAnyPermission('sap-sync-task-resynchron') && ['0', '-1'].includes(row.data.status) && !!row.data.key"
                        v-tooltip.bottom="row.data.status === '0' ? 'Syncron' : 'Resyncron'"
                        :icon="table.btnSyncLoadUsed.includes(row.data.id) ? 'pi pi-spin pi-spinner' : 'pi pi-sync'"
                        outlined rounded severity="info" size="small"
                        :disabled="table.btnSyncLoadUsed.includes(row.data.id)" @click="resyncron(row.data)" />
                      <ButtonPrime
                        v-if="hasAnyPermission('sap-sync-task-edit-form') && row.data.status === '-1' && !!row.data.key"
                        v-tooltip.bottom="'Edit'" icon="pi pi-pencil" outlined rounded size="small"
                        style="margin-left: 5px;" @click="edit(row.data)" />
                      <ButtonPrime v-if="hasAnyPermission('sap-sync-task-synchron-log-view')" v-tooltip.bottom="'Log'"
                        icon="pi pi-file-edit" outlined rounded severity="help" size="small" style="margin-left: 5px;"
                        @click="openLog(row.data)" />
                    </div>
                    <div v-else class="m-4"><span /></div>
                  </template>
                </Column>
                <template #empty>
                  <div style="text-align: center; padding: 35px 0;">
                    <img src="/no_data.png" alt="No Data" style="width: 330px; height: 330px; opacity: 0.7;" />
                    <p class="text-muted mt-2">No items found</p>
                  </div>
                </template>
              </DataTable>
            </div>
            <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
              <BasePagination v-model="table.page.current_pages" :page-count="table.page.total_pages" size="sm"
                @click="changePage()" />
              <p>
                {{ (table.page.current_pages - 1) * (table.page.per_pages || 10) + table.data.length }}
                of {{ table.total }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FILTER DIALOG -->
    <DialogPrime v-model:visible="modalFilter.visible" header="Synchron Task Filter"
      style="width: 35rem; margin: 10px;">
      <div class="mb-2">
        <label for="sap_sync" class="font-semibold">SAP Sync</label>
        <InputText id="sap_sync" v-model="modalFilter.data.sap_sync" placeholder="SAP Sync" fluid
          :disabled="modalFilter.action === 'edit'" />
      </div>

      <div class="mb-2">
        <label for="connection" class="font-semibold">Connection</label>
        <SelectPrime id="connection" v-model="modalFilter.data.connection" :options="connectionOptions"
          option-label="label" option-value="value" placeholder="Choose Connection" fluid
          :disabled="connectionLoading" />
      </div>

      <div class="mb-2">
        <label for="status" class="font-semibold">Status</label>
        <SelectPrime id="status" v-model="modalFilter.data.status" :options="statusOptions" option-label="label"
          option-value="value" placeholder="Choose Status" fluid />
      </div>

      <div class="mb-2">
        <label for="key" class="font-semibold">Key</label>
        <InputText id="key" v-model="modalFilter.data.key" placeholder="Key" fluid />
      </div>

      <div class="mb-2">
        <label for="requested_by" class="font-semibold">Requested By</label>
        <InputText id="requested_by" v-model="modalFilter.data.requested_by" placeholder="Requested By" fluid />
      </div>

      <div class="row mb-2 g-2">
        <div class="col-6">
          <label for="requested_at_start" class="font-semibold">Requested At Start</label>
          <Calendar id="requested_at_start" v-model="modalFilter.data.requested_at_start" date-format="yy-mm-dd"
            show-icon placeholder="YYYY-MM-DD" fluid />
        </div>
        <div class="col-6">
          <label for="requested_at_end" class="font-semibold">Requested At End</label>
          <Calendar id="requested_at_end" v-model="modalFilter.data.requested_at_end" date-format="yy-mm-dd" show-icon
            placeholder="YYYY-MM-DD" fluid />
        </div>
      </div>

      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Close" severity="danger" class="px-4 border-0" raised size="small"
            :disabled="modalFilter.onLoading" @click="modalFilter.visible = false" />
          <ButtonPrime type="button" severity="contrast" raised size="small" label="Filter" class="px-4"
            :disabled="modalFilter.onLoading" @click="filter">
            <template v-if="modalFilter.onLoading" #icon>
              <i class="fas fa-spinner fa-spin me-2"></i>
            </template>
          </ButtonPrime>
        </div>
      </template>
    </DialogPrime>

    <!-- LOG DIALOG -->
    <!-- maximizable: tombol perbesar / kecilkan (pola New_ItemChangeLogModal). Saat diperbesar tabel memakai tinggi layar. -->
    <DialogPrime v-model:visible="modalLog.visible" header="Synchron Task Log" maximizable :draggable="false"
      :style="{ width: 'min(55rem, 96vw)', margin: '10px' }" :breakpoints="{ '768px': '100vw' }"
      @maximize="modalLog.maximized = true" @unmaximize="modalLog.maximized = false">
      <!-- Identitas MCR dari baris list sync task (relasi `emcr`): MCR Code, MCR Number, Subject. -->
      <div v-if="modalLog.mcrCode" class="d-flex flex-wrap align-items-center gap-3 mb-3 small text-muted">
        <span class="d-flex align-items-center gap-2">
          <i class="pi pi-tag"></i>
          <span>MCR Number</span>
          <span class="fw-bold text-dark">{{ modalLog.emcr?.mcr_number || '-' }}</span>
        </span>
        <span class="d-flex align-items-center gap-2">
          <i class="pi pi-align-left"></i>
          <span>Description</span>
          <span class="fw-bold text-dark">{{ modalLog.emcr?.subject || '-' }}</span>
        </span>
      </div>
      <div :style="{ maxHeight: modalLog.maximized ? 'calc(100vh - 180px)' : '450px', overflowY: 'auto' }">
        <DataTable v-model:expanded-rows="modalLog.expandedRows" :value="modalLog.onLoad ? logSkeletonRows : modalLog.tasks"
          data-key="id" class="p-datatable-sm" table-style="min-width: 35rem" @row-expand="onLogRowExpand">
          <Column expander header-class="bg-light" style="width: 3rem" />
          <!-- Spesifikasi item (`emcr_item` task, key = mcr_item_code); task level MCR tanpa item -> "-". -->
          <Column header="Item Description" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="modalLog.onLoad" />
              <span v-else class="fw-semibold" :title="row.data.emcr_item?.specification || ''">
                {{ truncateText(row.data.emcr_item?.specification, 40) || '-' }}
              </span>
            </template>
          </Column>
          <Column header="Status" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="modalLog.onLoad" width="5rem" />
              <span v-else class="text-uppercase"
                :class="row.data.status === '1' ? 'badge badge-success' : row.data.status === '-1' ? 'badge badge-danger' : 'badge badge-secondary'">
                {{ row.data.status === '1' ? 'Success' : row.data.status === '-1' ? 'Failed' : 'Open' }}
              </span>
            </template>
          </Column>
          <Column header="Requested At" header-class="bg-light">
            <template #body="row">
              <Skeleton v-if="modalLog.onLoad" />
              <span v-else>{{ formatLogDate(row.data.requested_at) }}</span>
            </template>
          </Column>
          <template #expansion="{ data }">
            <div class="log-expansion">
              <div v-if="!modalLog.details[data.id] || modalLog.details[data.id].loading"
                class="d-flex align-items-center gap-2 text-muted small">
                <i class="pi pi-spin pi-spinner"></i>
                <span>Loading log...</span>
              </div>
              <div v-else-if="modalLog.details[data.id].failed" class="text-muted small">
                <i class="pi pi-exclamation-circle me-1"></i>Failed to load log detail.
              </div>
              <template v-else-if="modalLog.details[data.id].entries.length">
                <!-- Semua log failed + log success terakhir (kalau elemen terakhir response = success), urut lama -> baru. -->
                <div v-for="entry in modalLog.details[data.id].entries" :key="entry.id" class="log-entry"
                  :class="entry.type === 'failed' ? 'log-entry-failed' : 'log-entry-success'">
                  <div class="d-flex align-items-center justify-content-between gap-2 mb-1">
                    <div class="d-flex align-items-center gap-2">
                      <span class="badge text-uppercase" :class="entry.type === 'failed' ? 'badge-danger' : 'badge-success'">{{ entry.type }}</span>
                      <span class="small text-muted">{{ entry.date }}</span>
                    </div>
                    <button v-if="entry.payload" type="button" class="btn btn-sm btn-link p-0 m-0 small"
                      @click="togglePayload(entry.id)">
                      <i class="pi" :class="modalLog.openPayloads[entry.id] ? 'pi-eye-slash' : 'pi-code'"></i>
                      {{ modalLog.openPayloads[entry.id] ? 'Hide Payload' : 'Show Payload' }}
                    </button>
                  </div>
                  <ul v-if="entry.messages.length" class="log-error-list mb-0">
                    <li v-for="(message, i) in entry.messages" :key="i">{{ message }}</li>
                  </ul>
                  <div v-else class="small text-muted">No message.</div>
                  <pre v-if="entry.payload && modalLog.openPayloads[entry.id]" class="log-payload mb-0 mt-2">{{ entry.payload }}</pre>
                </div>
              </template>
              <div v-else class="d-flex align-items-center gap-2 text-muted small">
                <i class="pi pi-info-circle"></i>
                <span>No log to display for this task.</span>
              </div>
            </div>
          </template>
          <template #empty>
            <div style="text-align: center; padding: 35px 0;">
              <img src="/no_data.png" alt="No Data" style="width: 100px; height: 100px; opacity: 0.5;" />
              <p class="text-muted mt-2">No items found</p>
            </div>
          </template>
        </DataTable>
      </div>
    </DialogPrime>

    <!-- SAP CREDENTIAL DIALOG (ANTAM) -->
    <DialogPrime v-model:visible="modalSapCredential.visible" header="SAP Credential"
      style="width: 30rem; margin: 10px;">
      <div class="mb-2">
        <label for="sap_username" class="font-semibold">Username SAP <span class="text-danger">*</span></label>
        <InputText id="sap_username" v-model="modalSapCredential.data.username" placeholder="Username SAP"
          :class="`${(modalSapCredential.errorMessage.username && modalSapCredential.data.username === '') ? 'border-danger' : ''}`"
          fluid />
        <small v-if="modalSapCredential.errorMessage.username && modalSapCredential.data.username === ''"
          class="text-danger">{{
            modalSapCredential.errorMessage.username }}</small>
      </div>

      <div class="mb-2">
        <label for="sap_password" class="font-semibold">Password SAP <span class="text-danger">*</span></label>
        <Password id="sap_password" v-model="modalSapCredential.data.password" placeholder="Password SAP"
          :invalid="modalSapCredential.errorMessage.password && modalSapCredential.data.password === ''" toggle-mask
          fluid />
        <small v-if="modalSapCredential.errorMessage.password && modalSapCredential.data.password === ''"
          class="text-danger">{{
            modalSapCredential.errorMessage.password }}</small>
      </div>

      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime type="button" label="Close" severity="danger" class="px-4 border-0" raised size="small"
            :disabled="modalSapCredential.onLoading" @click="modalSapCredential.visible = false" />
          <ButtonPrime type="button" severity="contrast" raised size="small" label="Resyncron" class="px-4"
            :disabled="modalSapCredential.onLoading" @click="submitSapCredential">
            <template v-if="modalSapCredential.onLoading" #icon>
              <i class="fas fa-spinner fa-spin me-2"></i>
            </template>
          </ButtonPrime>
        </div>
      </template>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import BasePagination from "@/components/BasePagination.vue";
import api from "@/helpers/api";
import sapSyncTaskService from "@/services/sap/sapSyncTaskService";
import sapConnectionService from "@/services/sap/sapConnectionService";
import moment from "moment";
import NoAccess from "@/components/NoAccess.vue";

export default {
  components: {
    BasePagination,
    NoAccess,
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    connectionOptions() {
      return (this.connectionList || []).map(c => ({
        label: c.connection || c.code || c,
        value: c.connection || c.code || c
      }));
    },
    statusOptions() {
      return [
        { label: 'All', value: '' },
        { label: 'Open', value: '0' },
        { label: 'Success', value: '1' },
        { label: 'Failed', value: '-1' },
      ];
    },
  },
  data() {
    return {
      clientCode: '',
      companyCode: '',
      connectionList: [],
      connectionLoading: false,
      table: {
        loadTimeout: null,
        onLoad: true,
        btnSyncLoadUsed: [],
        search: this.$route.query.search ? this.$route.query.search : "",
        total: 0,
        data: [{}],
        page: {
          current_pages: 1,
          total_pages: 1,
          per_pages: 10,
        },
      },
      modalFilter: {
        visible: false,
        onLoading: false,
        action: "",
        data: {
          sap_sync: this.$route.query.sap_sync ?? "",
          connection: this.$route.query.connection ?? "",
          key: this.$route.query.key ?? "",
          requested_by: this.$route.query.requested_by ?? "",
          status: this.$route.query.status ?? "",
          requested_at_start: this.$route.query.requested_at_start ?? "",
          requested_at_end: this.$route.query.requested_at_end ?? ""
        },
      },
      modalLog: {
        visible: false,
        onLoad: false,
        mcrCode: "",
        // relasi `emcr` baris list sync task (mcr_number, subject) untuk header dialog
        emcr: null,
        tasks: [],
        expandedRows: {},
        // log-detail per id task, dimuat sekali saat baris pertama kali di-expand
        details: {},
        // id log yang payload-nya sedang dibuka (tombol Show Payload)
        openPayloads: {},
        // dialog sedang diperbesar (event maximize / unmaximize DialogPrime)
        maximized: false,
      },
      logSkeletonRows: [{ id: "skeleton-1" }, { id: "skeleton-2" }, { id: "skeleton-3" }],
      modalSapCredential: {
        visible: false,
        onLoading: false,
        data: { username: "", password: "" },
        errorMessage: {},
        pendingValues: null,
      },
    };
  },
  mounted() {
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.clientCode = userAuth?.user?.client_code || '';
    this.companyCode = userAuth?.user?.company_code || '';
    // Saat halaman dibuka cuma get() (daftar task company user, disaring BE). Opsi Connection dimuat saat modal Filter dibuka
    // (openFilter) dan TIDAK otomatis jadi filter -- dulu satu-satunya connection langsung terisi ke filter sehingga get() berikutnya
    // (mis. setelah Resynchron) ikut mengirim `connection`.
    if (this.hasAnyPermission('sap-sync-task-view')) {
      this.get();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    async fetchConnections() {
      this.connectionLoading = true;
      try {
        const client = this.companyCode === 'MIND-ID' ? null : (this.clientCode || null);
        const params = { limit: 1000 };
        if (client) params.client = client;
        const res = await api(this, sapConnectionService.list(params), false);
        const raw = res?.data;
        let list = [];
        if (Array.isArray(raw)) list = raw;
        else if (Array.isArray(raw?.data)) list = raw.data;
        else if (Array.isArray(raw?.data?.data)) list = raw.data.data;
        this.connectionList = list;
      } catch (e) {
        this.connectionList = [];
      } finally {
        this.connectionLoading = false;
      }
    },
    toYMD(val) {
      if (!val) return "";
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const d = val instanceof Date ? val : new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return moment(d).format("YYYY-MM-DD");
    },
    async get() {
      this.table.onLoad = true;
      try {
        const filterParams = { ...this.modalFilter.data };
        // Normalize date -> YYYY-MM-DD string
        if (filterParams.requested_at_start) filterParams.requested_at_start = this.toYMD(filterParams.requested_at_start);
        if (filterParams.requested_at_end) filterParams.requested_at_end = this.toYMD(filterParams.requested_at_end);
        if (filterParams.status === '' || filterParams.status == null) delete filterParams.status;
        if (!filterParams.requested_at_start) delete filterParams.requested_at_start;
        if (!filterParams.requested_at_end) delete filterParams.requested_at_end;
        if (!filterParams.sap_sync) delete filterParams.sap_sync;
        if (!filterParams.connection) delete filterParams.connection;
        if (!filterParams.key) delete filterParams.key;
        if (!filterParams.requested_by) delete filterParams.requested_by;
        const response = await api(this, sapSyncTaskService.list(this.table.page.current_pages, filterParams), false);
        const resData = response.data;
        if (Array.isArray(resData)) {
          this.table.data = resData;
          this.table.total = resData.length;
          this.table.page.total_pages = 1;
          this.table.page.per_pages = resData.length;
        } else {
          this.table.data = resData.data || [];
          this.table.total = resData.total_items || resData.total || 0;
          this.table.page.total_pages = resData.total_pages || resData.last_page || 1;
          this.table.page.per_pages = resData.per_pages || resData.per_page || 10;
        }
      } catch (error) {
        this.table.data = [];
        this.table.total = 0;
      } finally {
        this.table.onLoad = false;
      }
    },
    async openLog(task) {
      const mcrCode = task?.emcr?.mcr_code;
      if (!mcrCode) {
        this.$toastNotifPrime('warn', 'Warning', 'MCR data not found for this sync task.');
        return;
      }
      this.modalLog = { ...this.modalLog, visible: true, onLoad: true, mcrCode, emcr: task.emcr, tasks: [], expandedRows: {}, details: {}, openPayloads: {} };
      try {
        const response = await api(this, sapSyncTaskService.getTasksByMcrCode(mcrCode), false);
        this.modalLog.tasks = Array.isArray(response?.data) ? response.data : [];
      } catch (error) {
        this.modalLog.tasks = [];
      } finally {
        this.modalLog.onLoad = false;
        // baris skeleton yang sempat di-expand saat memuat tidak ikut terbawa
        this.modalLog.expandedRows = {};
      }
    },
    async onLogRowExpand({ data }) {
      const cached = this.modalLog.details[data?.id];
      // gagal dimuat -> dicoba lagi saat baris di-expand ulang
      if (this.modalLog.onLoad || !data?.id || (cached && !cached.failed)) return;
      this.modalLog.details[data.id] = { loading: true, failed: false, entries: [] };
      try {
        const response = await api(this, sapSyncTaskService.getSyncLog(data.id), false);
        const logs = Array.isArray(response?.data) ? response.data : [];
        this.modalLog.details[data.id] = { loading: false, failed: false, entries: this.buildLogEntries(logs) };
      } catch (error) {
        this.modalLog.details[data.id] = { loading: false, failed: true, entries: [] };
      }
    },
    // Semua log type failed + log success KALAU elemen terakhir response bertipe success (sync terakhir berhasil); requested /
    // executed / success lain dilewati, urut seperti response (BE urut id). success: `message` apa adanya + payload (Show Payload).
    // failed: `message` = JSON string
    // { status, [key task]: { resultIntegration, resultMPN, ... } } -- dibaca cuma key yang mengandung MCR Code (task per item:
    // "<mcr_code>-<suffix>"), lihat failedLogMessages. Tidak ada key MCR (mis. { status, response: "EMCR Item not found." }) ->
    // `response`. Bukan JSON -> teks message apa adanya. `payload` (JSON string yang dikirim ke SAP) dirapikan untuk Show Payload.
    buildLogEntries(logs) {
      const lastLog = logs[logs.length - 1];
      return logs
        .filter((log) => log?.type === 'failed' || (log === lastLog && log?.type === 'success'))
        .map((log) => ({
          id: log.id,
          type: log.type,
          date: log.created_at ? moment(log.created_at).format('DD-MM-YYYY HH:mm') : '-',
          messages: log.type === 'failed' ? this.failedLogMessages(log.message) : [String(log.message ?? '').trim()].filter(Boolean),
          payload: this.prettyJson(log.payload),
        }));
    },
    failedLogMessages(message) {
      let parsed;
      try {
        parsed = typeof message === 'string' ? JSON.parse(message) : message;
      } catch (e) {
        return message ? [String(message)] : [];
      }
      if (!parsed || typeof parsed !== 'object') return message ? [String(message)] : [];
      const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
      const errorMessages = (items) => toArray(items)
        .filter((item) => item?.TYPE === 'E' && item?.MESSAGE)
        .map((item) => String(item.MESSAGE).trim());
      // RETURNMESSAGES beda bentuk per company: INALUM { item: [...] }, ANTAM langsung array [...].
      const returnMessageItems = (value) => (Array.isArray(value) ? value : value?.item);
      const mcrCode = String(this.modalLog.mcrCode || '');
      const nodes = Object.entries(parsed).filter(([key, value]) => mcrCode && key.includes(mcrCode) && value && typeof value === 'object');
      const messages = [];
      nodes.forEach(([, node]) => {
        const integration = node.resultIntegration;
        if (typeof integration === 'string') {
          if (integration.trim()) messages.push(integration.trim());
        } else if (integration && typeof integration === 'object') {
          const found = integration.RETURNMESSAGES ? errorMessages(returnMessageItems(integration.RETURNMESSAGES)) : errorMessages(integration.RETURN);
          // Error dari middleware SAP (PI/PO): { error: { code, message, logID } }, message ter-encode entitas HTML (&#x20; ...).
          // Cuma bagian sebelum ":" pertama, mis. "Error while sending message to module processor".
          if (!found.length && typeof integration.error?.message === 'string' && integration.error.message.trim()) {
            found.push(this.decodeHtmlEntities(integration.error.message).split(':')[0].trim());
          }
          // Error dari BE sendiri: { status, message }, mis. SQLSTATE ... -- cuma bagian sebelum "DETAIL:".
          if (!found.length && typeof integration.message === 'string' && integration.message.trim()) {
            found.push(integration.message.split('DETAIL:')[0].trim());
          }
          messages.push(...found);
        }
        // Error MPN per manufacturer: resultMPN[].response.resultMPN -> RETURNMESSAGES TYPE E, fallback RETURN TYPE E.
        // Diberi awalan [MPN] supaya beda dengan error material utama.
        toArray(node.resultMPN).forEach((mpn) => {
          const result = mpn?.response?.resultMPN;
          if (result && typeof result === 'object') {
            const found = result.RETURNMESSAGES ? errorMessages(returnMessageItems(result.RETURNMESSAGES)) : [];
            (found.length ? found : errorMessages(result.RETURN)).forEach((text) => messages.push(`[MPN] ${text}`));
          }
          // AMPL (relasi material - MPN): resultMPN[].response.resultAMPL { status, payload, response }. Status E -> isi
          // `response`: teks (mis. error cURL, tanpa bagian "(see https://curl.se/...)") atau object SAP (RETURN/RETURNMESSAGES TYPE E).
          const ampl = mpn?.response?.resultAMPL;
          if (ampl?.status !== 'E') return;
          toArray(ampl.response).forEach((res) => {
            if (typeof res === 'string') {
              const text = res.replace(/\s*\(see https?:\/\/[^)]*\)/i, '').trim();
              if (text) messages.push(`[AMPL] ${text}`);
            } else if (res && typeof res === 'object') {
              const found = res.RETURNMESSAGES ? errorMessages(returnMessageItems(res.RETURNMESSAGES)) : [];
              (found.length ? found : errorMessages(res.RETURN)).forEach((text) => messages.push(`[AMPL] ${text}`));
            }
          });
        });
      });
      if (!nodes.length && typeof parsed.response === 'string' && parsed.response.trim()) messages.push(parsed.response.trim());
      return [...new Set(messages)];
    },
    // "Error&#x20;while&#x20;..." -> "Error while ..." (entitas HTML diterjemahkan browser, bukan dirender sebagai HTML).
    decodeHtmlEntities(text) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    },
    prettyJson(value) {
      if (value === null || value === undefined || value === '') return null;
      try {
        return JSON.stringify(typeof value === 'string' ? JSON.parse(value) : value, null, 2);
      } catch (e) {
        return String(value);
      }
    },
    // Potong teks panjang di batas kata terakhir sebelum maxLength (sama dengan New_UserForm.truncateText).
    truncateText(text, maxLength = 40) {
      const str = text ? String(text) : '';
      if (str.length <= maxLength) return str;
      const sliced = str.slice(0, maxLength);
      const lastSpace = sliced.lastIndexOf(' ');
      const safeSlice = lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced;
      return `${safeSlice} ...`;
    },
    togglePayload(id) {
      this.modalLog.openPayloads = { ...this.modalLog.openPayloads, [id]: !this.modalLog.openPayloads[id] };
    },
    resyncron(values) {
      this.confirmDialog("Are you sure to synchron this data?", "synchron it").then((result) => {
        if (result.isConfirmed) {
          if (values?.emcr?.company === 'ANTAM') {
            this.openSapCredentialModal(values);
          } else {
            this.doResynchron(values);
          }
        }
      });
    },
    openSapCredentialModal(values) {
      this.modalSapCredential = {
        visible: true,
        onLoading: false,
        data: { username: "", password: "" },
        errorMessage: {},
        pendingValues: values,
      };
    },
    submitSapCredential() {
      let isValid = true;
      const requiredField = [{ key: "username", message: "Username SAP" }, { key: "password", message: "Password SAP" }];
      requiredField.forEach((val) => {
        if (!this.modalSapCredential.data[val.key]) {
          this.modalSapCredential.errorMessage[val.key] = `${val.message} is required`;
          isValid = false;
        }
      });
      if (!isValid) return;

      const values = this.modalSapCredential.pendingValues;
      const params = {
        username: this.modalSapCredential.data.username,
        password: this.modalSapCredential.data.password,
      };

      this.modalSapCredential.visible = false;
      // Password tidak disimpan di state setelah dikirim.
      this.modalSapCredential.data.password = "";
      this.doResynchron(values, params);
    },
    // `credentials` (opsional): { username, password } dari modal kredensial SAP (ANTAM, submitSapCredential). Company lain
    // tanpa kredensial -> BE memakai kredensial SAP Connection task-nya.
    doResynchron(values, credentials = {}) {
      this.table.btnSyncLoadUsed.push(values.id);
      // Body { key: MCR Code, username?, password? } (lihat sapSyncTaskService.resynchron).
      api(this, sapSyncTaskService.resynchron(values?.emcr?.mcr_code || values?.key, credentials)).then(() => {
        this.get();
      }).finally(() => {
        const index = this.table.btnSyncLoadUsed.indexOf(values.id);
        if (index > -1) {
          this.table.btnSyncLoadUsed.splice(index, 1);
        }
      });
    },
    edit(values) {
      if (!this.hasAnyPermission('sap-sync-task-edit-form')) return;
      const emcr = values?.emcr;
      if (!emcr?.id || !emcr?.mcr_code) {
        this.$toastNotifPrime('warn', 'Warning', 'MCR data not found for this sync task.');
        return;
      }
      this.$router.push({
        name: 'MCR Form for Edit Sync Task',
        params: { moduleSlug: 'material-management', id: emcr.id, action: 'edit', mcrCode: emcr.mcr_code },
      });
    },
    openFilter() {
      this.modalFilter.visible = true;
      this.modalFilter.onLoading = false;
      if (!this.connectionList.length && !this.connectionLoading) this.fetchConnections();
    },
    filter() {
      this.modalFilter.onLoading = true;

      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        const qStart = this.toYMD(this.modalFilter.data.requested_at_start);
        const qEnd = this.toYMD(this.modalFilter.data.requested_at_end);
        this.$router.push({
          name: 'SAP Sync Task', params: { moduleSlug: 'material-management' }, query: {
            sap_sync: this.modalFilter.data.sap_sync ?? '',
            connection: this.modalFilter.data.connection ?? '',
            key: this.modalFilter.data.key ?? '',
            requested_by: this.modalFilter.data.requested_by ?? '',
            ...(this.modalFilter.data.status !== '' && this.modalFilter.data.status != null ? { status: String(this.modalFilter.data.status) } : {}),
            ...(qStart ? { requested_at_start: qStart } : {}),
            ...(qEnd ? { requested_at_end: qEnd } : {}),
          }
        });

        this.modalFilter.visible = false;
        this.modalFilter.onLoading = false;
        this.get();
      }, 500);
    },
    changePage() {
      this.table.onLoad = true;

      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        this.get();
      }, 100);
    },
    formatDate(dateString) {
      return dateString ? moment(dateString).format("DD/MM/YYYY HH:mm") : "-";
    },
    formatLogDate(dateString) {
      return dateString ? moment(dateString).format("DD - MM - YYYY HH:mm") : "-";
    },
  },
};
</script>

<style scoped>
.value-table-wrapped {
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.log-expansion {
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-left: 3px solid #adb5bd;
  border-radius: 0.375rem;
}

.log-expansion-error {
  background: #fff5f5;
  border-left-color: #f5365c;
}

.log-error-list {
  padding-left: 1.25rem;
  font-size: 0.85rem;
  color: #344767;
}

.log-error-list li {
  margin-bottom: 0.35rem;
  line-height: 1.4;
  word-break: break-word;
}

.log-error-list li:last-child {
  margin-bottom: 0;
}

.log-entry {
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 0.375rem;
  border-left: 3px solid #adb5bd;
  background: #fff;
}

.log-entry:last-child {
  margin-bottom: 0;
}

.log-entry-failed {
  border-left-color: #f5365c;
  background: #fff5f5;
}

.log-entry-success {
  border-left-color: #2dce89;
}

.log-payload {
  max-height: 260px;
  overflow: auto;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  line-height: 1.4;
  color: #e9ecef;
  background: #1e1e2e;
  border-radius: 0.375rem;
  white-space: pre;
}
</style>
